import * as React from 'react';
import { getIsAdmin, KeycloakContext } from '@coverright/shared/keycloak';
import {
  getCId,
  getStoredUser, resetStoredUser,
  setMedigapQuoteId, setPdpQuoteId,
  setQuoteId,
  setStoredUser
} from '@coverright/utils';
import { enrollmentApolloClient } from '@coverright/data-access/apollo-clients';
import { UserProfileInput, UserProfileOutput } from '@coverright/data-access/types/enrollment';
import {
  useMyClientIdLazyQuery,
  useSaveUserProfileMutation,
  useUserProfileLazyQuery
} from '@coverright/data-access/enrollment';

type IProfileContext = UserProfileOutput & {clientId?: string, save: (data: {variables:  {data: UserProfileInput}}) => any};

export const ProfileContext = React.createContext<IProfileContext>({save: () => null, profileId: ''});

export function ProfileProvider(props: React.PropsWithChildren<any>) {

  const {keycloak, initialized} = React.useContext(KeycloakContext);
  const [getProfile, {data: profileData}] = useUserProfileLazyQuery({client: enrollmentApolloClient});
  const [saveProfile] = useSaveUserProfileMutation({
    onCompleted: data => setStoredUser({...getStoredUser(), profileId: data?.saveUserProfile?.profileId || getStoredUser()?.profileId}),
    client: enrollmentApolloClient
  })
  const [profile, setProfile] = React.useState<UserProfileOutput>();
  const [clientId, setClientId] = React.useState<string>();
  const [getClientId] = useMyClientIdLazyQuery({client: enrollmentApolloClient});

  React.useEffect(() => {
    if (initialized) {
      if (keycloak?.authenticated) {
        if (!getIsAdmin(keycloak)) {
          getProfile().then(() => getClientId().then(res => {
            setStoredUser({...getStoredUser(), clientId: res.data?.myClientId || getStoredUser()?.clientId})
            setClientId(res.data?.myClientId)
          }));
        }
      } else if (getStoredUser()?.profileId) {
        resetStoredUser();
      }
    }
  }, [keycloak?.authenticated, initialized]);

  React.useEffect(() => {
    if (profileData?.userProfile) {
      setStoredUser({
        email: profileData?.userProfile?.email || undefined,
        firstName: profileData?.userProfile?.firstName || undefined,
        lastName: profileData?.userProfile?.lastName || undefined,
        zip: profileData?.userProfile?.zip || undefined,
        gender: profileData?.userProfile?.gender || undefined,
        tobacco: typeof profileData?.userProfile?.tobacco !== 'undefined' ? String(profileData?.userProfile?.tobacco) : undefined,
        countyName: profileData?.userProfile?.countyName || undefined,
        birthDate: profileData?.userProfile?.birthDate || undefined,
        profileId: profileData?.userProfile?.profileId || undefined,
        phoneNumber: profileData?.userProfile?.phoneNumber || undefined,
        state: profileData?.userProfile?.state || undefined,
        userId: profileData?.userProfile?.userId || undefined,
      })
      const urlParams = new URLSearchParams(window.location.search);
      if (profileData?.userProfile?.quoteId && !urlParams.has('adminQuote')) {
        setQuoteId(profileData?.userProfile?.quoteId);
      }
      if (profileData?.userProfile?.medigapQuoteId && !urlParams.has('adminQuote')) {
        setMedigapQuoteId(profileData?.userProfile?.medigapQuoteId);
      }
      if (profileData?.userProfile?.pdpQuoteId && !urlParams.has('adminQuote')) {
        setPdpQuoteId(profileData?.userProfile?.pdpQuoteId);
      }
    }
    setProfile(profileData?.userProfile || undefined)
  }, [profileData]);

  return <ProfileContext.Provider value={{...profile, clientId, save: saveProfile} as any}>
    {props.children}
  </ProfileContext.Provider>

}

export const withProfileContext = (WrappedComponent: any) => (props: any) => {
  return (
    <ProfileProvider>
      <WrappedComponent {...props} />
    </ProfileProvider>
  )
}
